import { useState } from 'react';
import { Button, Collapse, Switch, Typography } from 'antd';
import type { FilterDefinition, UseFilterStateReturn } from '../types';
import { FilterRenderer } from '../components/FilterRenderer';

const { Text } = Typography;
const { Panel } = Collapse;

interface FilterOption {
	value: string;
	label: string;
}

interface FilterOptionGroup {
	label: string;
	options: FilterOption[];
}

interface ResolvedOptionsMap {
	[filterKey: string]: FilterOption[] | undefined;
}

interface ResolvedOptionGroupsMap {
	[filterKey: string]: FilterOptionGroup[] | undefined;
}

interface Props {
	definitions: FilterDefinition[];
	filterState: UseFilterStateReturn;
	resolvedOptions?: ResolvedOptionsMap;
	resolvedOptionGroups?: ResolvedOptionGroupsMap;
	onResetAll: () => void;
	defaultActiveKeys?: string[];
	showValueSummary?: boolean;
	onToggleValueSummary?: (value: boolean) => void;
}

export const DesktopFilterSidebar = ({
	definitions,
	filterState,
	resolvedOptions = {},
	resolvedOptionGroups = {},
	onResetAll,
	defaultActiveKeys = ['shape'],
	showValueSummary = false,
	onToggleValueSummary,
}: Props) => {
	const [activeKeys, setActiveKeys] = useState<string[]>(defaultActiveKeys);

	// Only show filters configured for 'desktop' context and that pass visibility check
	const visibleFilters = definitions.filter(
		(def) => def.contexts.includes('desktop') && filterState.isVisible(def.key)
	);

	return (
		<div
			style={{
				background: '#FBFBFB',
				border: '1px solid #D9D9D9',
				borderRadius: 2,
				flex: '0 0 250px',
			}}
		>
			{/* Header */}
			<div
				style={{
					background: '#FBFBFB',
					borderBottom: '1px solid #D9D9D9',
					borderRadius: 2,
				}}
			>
				<div className="flex justify-between items-center pl-3 py-1">
					<Text strong style={{ margin: 0 }}>
						Filters
					</Text>
					<Button type="link" onClick={onResetAll}>
						Reset
					</Button>
				</div>
			</div>

			{/* Filter panels */}
			<Collapse
				activeKey={activeKeys}
				onChange={(keys) => setActiveKeys(keys as string[])}
				style={{ border: 'none' }}
				ghost
			>
				{visibleFilters.map((def) => (
					<Panel
						key={def.key}
						header={
							def.resettable !== false ? (
								<div className="flex flex-row justify-between w-full">
									{def.label}
									<Button
										type="link"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											if (def.key === 'shape' && resolvedOptions['shape']) {
												// "All shapes" = no shape param (empty), not all IDs
												filterState.setAndApply(def.key, []);
											} else {
												filterState.resetFilter(def.key);
											}
										}}
									>
										Reset
									</Button>
								</div>
							) : (
								def.label
							)
						}
						style={{
							width: '100%',
							backgroundColor:
								def.type === 'radio' && !def.resettable
									? 'transparent'
									: 'white',
							borderBottom: '1px solid #d9d9d9',
						}}
					>
						<FilterRenderer
							definition={def}
							filterState={filterState}
							resolvedOptions={resolvedOptions[def.key]}
							resolvedOptionGroups={resolvedOptionGroups[def.key]}
							compact={false}
						/>
					</Panel>
				))}
			</Collapse>

			{/* Show Value: toggle to show/hide sum of value for filtered lengths */}
			{onToggleValueSummary && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '12px 16px',
						borderTop: '1px solid #D9D9D9',
					}}
				>
					<Text style={{ fontSize: 13 }}>Show Value</Text>
					<Switch
						size="small"
						checked={showValueSummary}
						onChange={onToggleValueSummary}
					/>
				</div>
			)}
		</div>
	);
};
